import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PrimaryActionButton from './PrimaryActionButton';
import SecondaryActionButton from './SecondaryActionButton';

export interface PopupDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  showCloseButton?: boolean;
  disableEscapeKeyDown?: boolean;
  
  // Action buttons configuration
  primaryAction?: {
    label: string;
    onClick: () => void | Promise<void>;
    disabled?: boolean;
    loading?: boolean;
    variant?: 'contained' | 'outlined' | 'text';
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  };
  
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'contained' | 'outlined' | 'text';
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  };
  
  // Additional actions (for more complex dialogs)
  additionalActions?: React.ReactNode;
  
  // Custom styling
  dialogProps?: Record<string, any>;
  contentProps?: Record<string, any>;
  actionsProps?: Record<string, any>;
}

/**
 * PopupDialog - Reusable Dialog Component
 * 
 * A flexible and reusable dialog component that provides:
 * - Consistent dialog structure and styling
 * - Configurable action buttons with loading states
 * - Support for primary, secondary, and additional actions
 * - Customizable close behavior
 * - Built-in loading indicators
 * - Responsive design with configurable sizes
 */
const PopupDialog: React.FC<PopupDialogProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  showCloseButton = true,
  disableEscapeKeyDown = false,
  primaryAction,
  secondaryAction,
  additionalActions,
  dialogProps = {},
  contentProps = {},
  actionsProps = {},
}) => {
  const hasActions = primaryAction || secondaryAction || additionalActions;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disablePortal
      {...dialogProps}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: showCloseButton ? 1 : 3,
        }}
      >
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            disabled={primaryAction?.loading}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent {...contentProps}>
        {children}
      </DialogContent>

      {hasActions && (
        <DialogActions sx={{ px: 3, py: 2, ...actionsProps.sx }} {...actionsProps}>
          {/* Additional custom actions */}
          {additionalActions}
          
          {/* Secondary action button */}
          {secondaryAction && (
            <SecondaryActionButton
              label={secondaryAction.label}
              onClick={secondaryAction.onClick}
              variant={secondaryAction.variant}
              color={secondaryAction.color}
              disabled={secondaryAction.disabled || primaryAction?.loading}
              startIcon={<CloseIcon />}
            />
          )}

          {/* Primary action button with loading state */}
          {primaryAction && (
            <PrimaryActionButton
              label={primaryAction.label}
              onClick={primaryAction.onClick}
              variant={primaryAction.variant}
              color={primaryAction.color}
              disabled={primaryAction.disabled}
              loading={primaryAction.loading}
            />
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PopupDialog;